/**
 * W5 — Audit-log viewer page (`/audit-log`).
 *
 * Owner/team_lead-only window into the organisation's audit trail. Renders the
 * `GET /api/v1/agency/audit-log` feed with a filter bar (shop / employee /
 * action / date range) and pagination driven by the response `meta` block.
 *
 * Shell matches `Stats.tsx` / `Integrations.tsx`:
 *   PageWrapperMain → subPage → SubPageContentHolder → SubPageInnerContentHolder.
 * NO MUI primitives — plain divs + custom SCSS (`audit-log/auditLog.scss`) so
 * the page picks up the legacy theme variables.
 *
 * Role-gated to the `audit.view` capability. Other roles see an inline
 * "no permission" message inside the legacy chrome (so the sidebar stays
 * mounted and they can navigate away).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/audit-log/auditLog.scss';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { RootState } from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import { useCan, useAccessibleShops } from '../../lib/rbac';
import {
  AuditLogEntry,
  AuditLogMeta,
  AuditLogFilters,
  listAuditLog,
} from '../../lib/api/audit/audit-api';
import { AgencyEmployee, listEmployees } from '../../lib/api/identity/identity-api';

const PER_PAGE = 25;

const ACTION_OPTIONS = [
  'proposal.created',
  'proposal.approved',
  'proposal.denied',
  'proposal.auto_approved',
  'proposal.executed',
  'proposal.execution_failed',
  'employee.invited',
  'employee.removed',
  'shop.connected',
  'shop.disconnected',
];

// ---------- helpers ----------

/** Format an ISO timestamp into a compact, locale-aware string. */
function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Humanise an actor token. `user:agency:{id}` resolves to an employee name
 * when we can find one in the supplied lookup; otherwise the raw token is
 * returned (so nothing is ever hidden). `system` / unknown forms pass through.
 */
function humaniseActor(
  actor: string,
  employeeNameById: Record<string, string>
): { label: string; raw: string | null } {
  if (!actor) return { label: '—', raw: null };
  const match = /^user:agency:(.+)$/.exec(actor);
  if (match) {
    const id = match[1];
    const name = employeeNameById[id];
    if (name) return { label: name, raw: actor };
    return { label: actor, raw: null };
  }
  if (actor === 'system') return { label: 'System', raw: null };
  return { label: actor, raw: null };
}

/** Humanise an action token (e.g. `proposal.auto_approved` → `Proposal · auto approved`). */
function humaniseAction(action: string): string {
  if (!action) return '—';
  return action
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .join(' · ');
}

function riskModifier(risk: string | null): string {
  const r = (risk || '').toLowerCase();
  if (r === 'low') return 'auditLogRisk--low';
  if (r === 'medium') return 'auditLogRisk--medium';
  if (r === 'high') return 'auditLogRisk--high';
  if (r === 'critical') return 'auditLogRisk--critical';
  return '';
}

/**
 * Render an audit `detail` safely. The backend sends it as a string for some
 * events and as a small object (e.g. `{ notes }`, `{ reason }`,
 * `{ error, result }`) for others. Rendering a raw object as a React child
 * throws React error #31 — and on dev, where the error boundary is a
 * passthrough (see index.tsx), that one throw unmounts the whole app and the
 * page goes blank. Collapse objects to a compact `key: value` summary.
 */
function formatDetail(
  detail: string | Record<string, unknown> | null
): string {
  if (detail == null) return '—';
  if (typeof detail === 'string') return detail.trim() || '—';
  if (typeof detail === 'object') {
    const parts = Object.entries(detail)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(
        ([k, v]) =>
          `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`
      );
    return parts.length > 0 ? parts.join(' · ') : '—';
  }
  return String(detail);
}

// ---------- sub-components ----------

const ActorCell: React.FC<{
  actor: string;
  employeeNameById: Record<string, string>;
}> = ({ actor, employeeNameById }) => {
  const { label, raw } = humaniseActor(actor, employeeNameById);
  return (
    <div>
      <div className='auditLogTable__actor' title={raw || label}>
        {label}
      </div>
      {raw && <div className='auditLogTable__actorRaw'>{raw}</div>}
    </div>
  );
};

const Pagination: React.FC<{
  meta: AuditLogMeta;
  onPageChange: (page: number) => void;
  loading: boolean;
}> = ({ meta, onPageChange, loading }) => {
  const { t } = useTranslation();
  const current = meta.current_page || 1;
  const last = Math.max(1, meta.last_page || 1);
  const from = meta.total === 0 ? 0 : (current - 1) * meta.per_page + 1;
  const to = Math.min(meta.total, current * meta.per_page);

  return (
    <div className='auditLogPagination'>
      <div className='auditLogPagination__info'>
        {t('AuditLogPaginationInfo', {
          from,
          to,
          total: meta.total,
        })}
      </div>
      <div className='auditLogPagination__controls'>
        <button
          type='button'
          className='auditLogPagination__btn'
          disabled={loading || current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          {t('Previous')}
        </button>
        <span className='auditLogPagination__page'>
          {t('AuditLogPageOf', { current, last })}
        </span>
        <button
          type='button'
          className='auditLogPagination__btn'
          disabled={loading || current >= last}
          onClick={() => onPageChange(current + 1)}
        >
          {t('Next')}
        </button>
      </div>
    </div>
  );
};

// ---------- main ----------

const AuditLog: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const canView = useCan('audit.view');
  const accessibleShops = useAccessibleShops();

  // Resolve the full accessible-shop list (owner sees all → sourced from the
  // identity payload). Used both for the shop filter dropdown AND to humanise
  // shop slugs in rows that lack a `shop_name`.
  const identity = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity
  );
  const shopOptions = useMemo(() => {
    const fromShops = (identity?.accessible_shops ?? []).map((s) => ({
      slug: s.slug ?? s.brand_slug ?? '',
      name: s.name ?? s.brand_name ?? s.slug ?? s.brand_slug ?? '',
    }));
    const fromBrands = (identity?.accessible_brands ?? []).map((b) => ({
      slug: b.brand_slug ?? '',
      name: b.brand_name ?? b.brand_slug ?? '',
    }));
    const merged = [...fromShops, ...fromBrands].filter((s) => s.slug);
    // De-dupe by slug; non-owners are additionally clamped to their scope.
    const seen = new Set<string>();
    return merged.filter((s) => {
      if (seen.has(s.slug)) return false;
      seen.add(s.slug);
      if (accessibleShops === null) return true; // owner: all
      return accessibleShops.includes(s.slug);
    });
  }, [identity, accessibleShops]);

  // Employee dropdown source — fetched once on mount from the identity API.
  // Drives the Employee filter <select> (replacing the old free-text input) and
  // enriches actor humanisation in the table.
  const [employees, setEmployees] = useState<AgencyEmployee[]>([]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    listEmployees()
      .then((list) => {
        if (!cancelled) setEmployees(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[AuditLog] failed to load employees', err);
        if (!cancelled) setEmployees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  // Best-effort employee-name lookup for actor humanisation. Seeded with the
  // acting employee and enriched with the full employee list once it loads, so
  // `user:agency:{id}` actors resolve to a readable name.
  const employeeNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (identity?.employee_id && identity?.name) {
      map[String(identity.employee_id)] = identity.name;
    }
    employees.forEach((emp) => {
      if (emp.id) map[String(emp.id)] = emp.name || emp.email || String(emp.id);
    });
    return map;
  }, [identity, employees]);

  // ----- filter state -----
  const [shop, setShop] = useState('');
  const [employee, setEmployee] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  // ----- data state -----
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [meta, setMeta] = useState<AuditLogMeta>({
    total: 0,
    per_page: PER_PAGE,
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (overridePage?: number) => {
      setLoading(true);
      setError(null);
      const filters: AuditLogFilters = {
        shop: shop || undefined,
        employee: employee || undefined,
        action: action || undefined,
        from: from || undefined,
        to: to || undefined,
        page: overridePage ?? page,
        per_page: PER_PAGE,
      };
      try {
        const res = await listAuditLog(filters);
        setEntries(res.data);
        setMeta(res.meta);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AuditLog] load failed', err);
        const msg =
          err instanceof Error ? err.message : 'Failed to load audit log.';
        setError(msg);
        dispatch(setMessage({ message: msg, messageType: 'error' }));
      } finally {
        setLoading(false);
      }
    },
    [shop, employee, action, from, to, page, dispatch]
  );

  // Initial + page-change load. Filter changes go through `applyFilters`
  // (which resets the page to 1) to avoid double-fetching.
  useEffect(() => {
    if (canView) {
      load(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, page]);

  const applyFilters = () => {
    if (page === 1) {
      load(1);
    } else {
      setPage(1); // triggers the effect above
    }
  };

  const clearFilters = () => {
    setShop('');
    setEmployee('');
    setAction('');
    setFrom('');
    setTo('');
    if (page === 1) {
      // Re-fetch with cleared filters on the next tick once state has settled.
      setTimeout(() => load(1), 0);
    } else {
      setPage(1);
    }
  };

  const handlePageChange = (next: number) => {
    setPage(next);
  };

  /* ------------------------------------------------------------
     Shell wrapper. When `embedded` (hosted inside the Log Center
     tabbed page) we render only the inner content — the Log Center
     owns the PageWrapperMain/SubPage chrome and the title. Standalone
     (legacy `/audit-log` route) keeps the full shell.
     ------------------------------------------------------------ */
  const wrapShell = (content: React.ReactNode) =>
    embedded ? (
      <>{content}</>
    ) : (
      <PageWrapperMain>
        <div className='subPage'>
          <SubPageContentHolder>
            <SubPageInnerContentHolder
              title={t('AuditLog')}
              subTitle={t('AuditLogSubTitle')}
              backgroundColor={'var(--main-background-color)'}
            >
              {content}
            </SubPageInnerContentHolder>
          </SubPageContentHolder>
        </div>
      </PageWrapperMain>
    );

  /* ============================================================
     No-access state — render the legacy shell so the sidebar stays
     mounted and the user can navigate away.
     ============================================================ */
  if (!canView) {
    return wrapShell(
      <div className='auditLogPage'>
        <div className='auditLogPage__noAccess'>{t('AuditLogNoAccess')}</div>
      </div>
    );
  }

  /* ============================================================
     Main view
     ============================================================ */
  return wrapShell(
    <div className='auditLogPage'>
      {/* ---------- Filter bar ---------- */}
              <div className='auditLogFilters'>
                <div className='auditLogFilters__row'>
                  <div className='auditLogFilters__field'>
                    <label className='auditLogFilters__label'>
                      {t('AuditLogFilterShop')}
                    </label>
                    <select
                      className='auditLogFilters__select'
                      value={shop}
                      onChange={(e) => setShop(e.target.value)}
                    >
                      <option value=''>{t('AuditLogAllShops')}</option>
                      {shopOptions.map((s) => (
                        <option key={s.slug} value={s.slug}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='auditLogFilters__field'>
                    <label className='auditLogFilters__label'>
                      {t('AuditLogFilterEmployee')}
                    </label>
                    <select
                      className='auditLogFilters__select'
                      value={employee}
                      onChange={(e) => setEmployee(e.target.value)}
                    >
                      <option value=''>{t('AuditLogAllEmployees')}</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name || emp.email || emp.id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='auditLogFilters__field'>
                    <label className='auditLogFilters__label'>
                      {t('AuditLogFilterAction')}
                    </label>
                    <select
                      className='auditLogFilters__select'
                      value={action}
                      onChange={(e) => setAction(e.target.value)}
                    >
                      <option value=''>{t('AuditLogAllActions')}</option>
                      {ACTION_OPTIONS.map((a) => (
                        <option key={a} value={a}>
                          {humaniseAction(a)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='auditLogFilters__field'>
                    <label className='auditLogFilters__label'>
                      {t('AuditLogFilterFrom')}
                    </label>
                    <input
                      className='auditLogFilters__input'
                      type='date'
                      value={from}
                      max={to || undefined}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                  </div>

                  <div className='auditLogFilters__field'>
                    <label className='auditLogFilters__label'>
                      {t('AuditLogFilterTo')}
                    </label>
                    <input
                      className='auditLogFilters__input'
                      type='date'
                      value={to}
                      min={from || undefined}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </div>
                </div>

                <div className='auditLogFilters__actions'>
                  <MainSquareButton
                    value={t('AuditLogApplyFilters')}
                    type='button'
                    normal
                    onClick={applyFilters}
                  />
                  <button
                    type='button'
                    className='auditLogFilters__clearBtn'
                    onClick={clearFilters}
                  >
                    {t('AuditLogClearFilters')}
                  </button>
                </div>
              </div>

              {error && (
                <div className='auditLogPage__error' role='alert'>
                  <span>{error}</span>
                  <button
                    type='button'
                    className='auditLogPage__retryBtn'
                    onClick={() => load(page)}
                  >
                    {t('Retry')}
                  </button>
                </div>
              )}

              {/* ---------- Entry table ---------- */}
              {loading && entries.length === 0 ? (
                <div className='auditLogPage__loading'>
                  <LoadingSpinner customLoadingMsg={t('LoadingAuditLog')} />
                </div>
              ) : (
                <div className='auditLogTable'>
                  <div className='auditLogTable__head'>
                    <div className='auditLogTable__cell'>
                      {t('AuditLogColTime')}
                    </div>
                    <div className='auditLogTable__cell'>
                      {t('AuditLogColActor')}
                    </div>
                    <div className='auditLogTable__cell'>
                      {t('AuditLogColAction')}
                    </div>
                    <div className='auditLogTable__cell'>
                      {t('AuditLogColShop')}
                    </div>
                    <div className='auditLogTable__cell'>
                      {t('AuditLogColPlatform')}
                    </div>
                    <div className='auditLogTable__cell'>
                      {t('AuditLogColRisk')}
                    </div>
                    <div className='auditLogTable__cell'>
                      {t('AuditLogColDetail')}
                    </div>
                  </div>

                  {entries.length === 0 ? (
                    <div className='auditLogTable__empty'>
                      {t('AuditLogEmpty')}
                    </div>
                  ) : (
                    entries.map((entry) => (
                      <div className='auditLogTable__row' key={entry.id}>
                        <div
                          className='auditLogTable__cell auditLogTable__time'
                          data-label={t('AuditLogColTime')}
                        >
                          {formatTimestamp(entry.created_at)}
                        </div>
                        <div
                          className='auditLogTable__cell'
                          data-label={t('AuditLogColActor')}
                        >
                          <ActorCell
                            actor={entry.actor}
                            employeeNameById={employeeNameById}
                          />
                        </div>
                        <div
                          className='auditLogTable__cell auditLogTable__action'
                          data-label={t('AuditLogColAction')}
                        >
                          {humaniseAction(entry.action)}
                        </div>
                        <div
                          className='auditLogTable__cell auditLogTable__shop'
                          data-label={t('AuditLogColShop')}
                          title={entry.shop_name || entry.shop_slug || ''}
                        >
                          {entry.shop_name || entry.shop_slug || '—'}
                        </div>
                        <div
                          className='auditLogTable__cell'
                          data-label={t('AuditLogColPlatform')}
                        >
                          {entry.platform ? (
                            <span className='auditLogChip'>
                              {entry.platform}
                            </span>
                          ) : (
                            '—'
                          )}
                        </div>
                        <div
                          className='auditLogTable__cell'
                          data-label={t('AuditLogColRisk')}
                        >
                          {entry.risk_level ? (
                            <span
                              className={`auditLogRisk ${riskModifier(
                                entry.risk_level
                              )}`}
                            >
                              {entry.risk_level}
                            </span>
                          ) : (
                            '—'
                          )}
                        </div>
                        <div
                          className='auditLogTable__cell auditLogTable__detail'
                          data-label={t('AuditLogColDetail')}
                        >
                          {formatDetail(entry.detail)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

      {/* ---------- Pagination ---------- */}
      {entries.length > 0 && (
        <Pagination
          meta={meta}
          onPageChange={handlePageChange}
          loading={loading}
        />
      )}
    </div>
  );
};

export default AuditLog;
